<svelte:options
  customElement={{
    tag: "oc-expander-v1",
    /*                                            */
    extend: window.__components.extend(),
    props: {
      variant: { type: "String" },
      cutoffHeight: { type: "Number", attribute: "cutoff-height" },
      expanded: { type: "Boolean" },
      overflows: { type: "Boolean" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      hideFade: { type: "Boolean", attribute: "hide-fade" },
    },
  }}
/>

<script lang="ts">
  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import { untrack } from "svelte";
  import type { Events, Props } from "./ExpanderV1.types";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    variant = "row",
    cutoffHeight = 150,
    expanded = false,
    /*                                                         */
    overflows,
    hideFade = false,
    isCutOff = false,
    ocAriaLabel,
  }: Props = $props();

  const Host = $host();
  const dispatch = useEventDispatcher<Events>(Host);
  let clientHeight: number = $state(0);

  $effect(() => {
    overflows = isCutOff && !untrack(() => expanded);
    dispatch(
      "oc-expander-content-overflow-change",
      { overflows, cutoffHeight },
      { cancelable: false, alias: ["oc-content-overflow-change"] },
    );
    isCutOff = clientHeight > (cutoffHeight ?? 150);
  });

  /**
 *
 *
 */
  let displayFade = $derived(isCutOff && !expanded);

  function onClick(ev: MouseEvent): void {
    ev.preventDefault();

    const nextExpanded = !expanded;

    /*                                   */
    /*                         */
    if (
      !dispatch(
        nextExpanded ? "oc-expander-open" : "oc-expander-close",
        { hideFade, variant },
        {
          alias: [nextExpanded ? "oc-open" : "oc-close"],
        },
      )
    ) {
      return;
    }

    /*                                                  */
    expanded = nextExpanded;

    if (!expanded) {
      let top = Host.getBoundingClientRect().top;
      if (top < 0) {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          Host.scrollIntoView({ block: "start", behavior: "instant" });
        } else {
          Host.scrollIntoView({ block: "start", behavior: "smooth" });
        }
      }
    }
  }
</script>

<div class={`expander expander--variant-${variant}`}>
  <div class="expander__container">
    {#if isCutOff}
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <oc-row-v2
        onclick={onClick}
        as-button
        class="trigger"
        hide-divider={variant === "row" && (expanded || (!expanded && hideFade)) ? undefined : true}
        aria-expanded={expanded}
        aria-controls="content"
      >
        <!-- This element is used to capture click events for expanding/collapsing, we moved the fade effect into the expander__content element because Safari can't handle mask linear-gradients on empty divs -->
        <div class="clickable"></div>
        <div class="trigger__text">
          {#if expanded}
            <slot name="show-less-text">Weniger anzeigen</slot>
          {:else}
            <slot name="show-more-text">Mehr anzeigen</slot>
          {/if}
          <oc-icon-v1 type="arrow-down"></oc-icon-v1>
        </div>
      </oc-row-v2>
    {/if}
    <section
      class="expander__content"
      class:expander__content--with-fade={displayFade && !hideFade}
      class:expander__content--open={expanded}
      style:--cutoff-height="{cutoffHeight}px"
      id="content"
      aria-label={ocAriaLabel}
      aria-hidden={isCutOff && !expanded}
      inert={isCutOff && !expanded}
    >
      <div bind:clientHeight>
        <slot />
      </div>
    </section>
  </div>
</div>

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  :global {
    :host {
      --background-color: #{tokens.$oc-component-expander-background-color};
    }

    .expander {
      display: flex; /*                                       */
      background: var(--background-color);

      .trigger {
        font: tokens.$oc-component-expander-font;

        &__text {
          display: flex;
          justify-content: space-between;
          > slot {
            color: tokens.$oc-component-expander-text-color;
            display: block;
            flex: 1;
          }

          & > oc-icon-v1 {
            --color: #{tokens.$oc-component-expander-icon-color};
            flex: 0 0 auto;

            @media (prefers-reduced-motion: no-preference) {
              transition: transform tokens.$oc-component-expander-transition-duration
                tokens.$oc-component-expander-transition-easing;
            }
          }
        }

        &[aria-expanded="true"] oc-icon-v1 {
          transform: rotate(-180deg);
        }
      }

      &--variant-text {
        .trigger__text {
          flex-direction: row-reverse;
          gap: tokens.$oc-component-expander-gap-x;
        }
      }
    }

    .expander__container {
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .expander__content {
      position: relative;
      max-height: var(--cutoff-height);
      overflow: hidden;
      order: -1;

      @media (prefers-reduced-motion: no-preference) {
        transition: max-height tokens.$oc-component-expander-transition-duration
          tokens.$oc-component-expander-transition-easing;
      }
    }

    .expander__content--open {
      max-height: 100%;
      overflow: visible;
    }

    .expander__content--with-fade {
      /*                           */
      mask: linear-gradient(
        0deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.75) 49.93%,
        rgba(255, 255, 255, 1) 99.85%
      );
    }

    /*                                                        */
    .clickable {
      top: -6.25rem;
      cursor: pointer;
      height: tokens.$oc-component-expander-fade-height;
      left: 0;
      position: absolute;
      width: 100%;
    }
  }
</style>
